import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  ENDPOINTS,
  STORAGE_QUOTA_UNIT,
  THRESHOLD_TYPES,
  URLS,
} from '@shared/constants';
import {
  CommonResponseDTO,
  IBackblazeConfigResponse,
  IExternalStorageConfig,
  IStorageQuota,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';

export enum DMS_STATUS {
  INTERNAL = 'INTERNAL', // internal Storage is in use
  EXTERNAL = 'EXTERNAL', // external Storage is in use
  INTERNAL_PENDING = 'INTERNAL_PENDING', // internal Storage is in use
  EXTERNAL_PENDING = 'EXTERNAL_PENDING', // external Storage is in use
  INTERNAL_DISCONNECTED = 'INTERNAL_DISCONNECTED', // internal Storage is in use
}

export enum CONFIG_STATUS {
  ACTIVATED = 'ACTIVATED',
  DEACTIVATED = 'DEACTIVATED',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
}
enum EXTERNAL_STORAGE_LOCATION_ACTIONS {
  DELETE = 'DELETE',
  ENABLE = 'ENABLE',
  INTERNAL_ENABLE = 'INTERNAL_ENABLE',
  EDIT = 'EDIT',
}

export enum BACKBLAZE_CONFIG_STATUS {
  ACTIVATED = 'ACTIVATED',
  DEACTIVATED = 'DEACTIVATED',
}

export interface Config {
  url?: string;
  description?: string;
  status?: CONFIG_STATUS;
  connected_on?: string;
  deactivated_on?: string;
  valid_till?: string;
}

export interface StorageRecycleBinRequestDTO {
  recycle_bin_enabled: boolean;
  recycle_bin_number_of_days: number;
}

export interface StorageServiceURLRequestDTO {
  syncfusion_service_url: string;
}

export interface StorageQuotaRequestDTO {
  quota: number;
  defaultQuota?: number;
  threshold_value: number;
  is_enabled: boolean;
  is_exceeded: boolean;
  quota_unit: STORAGE_QUOTA_UNIT;
  defaultQuota_unit?: STORAGE_QUOTA_UNIT;
  threshold_unit: STORAGE_QUOTA_UNIT;
  threshold_type: THRESHOLD_TYPES;
  storage_size: number;
}

export interface UserStorageQuotaRequestDTO {
  is_user_enabled: boolean;
  user_quota: number;
  user_quota_unit: STORAGE_QUOTA_UNIT;
}

export interface QuotaUpdateRequestDTO {
  storage_quota: StorageQuotaRequestDTO;
  user_quota?: UserStorageQuotaRequestDTO;
}

export interface ManagePartitionRequestDTO {
  partitions: PartitionDTO[];
}

export class PartitionDTO {
  name: string;
  description: string;
  allocated_quota: number;
  quota_unit: string;
  threshold_limit: number;
  threshold_unit: string;
  threshold_type: string;
  storageId: string;
}

export interface StorageQuotaResponseDTO {
  quota: number;
  threshold_value: number;
  is_enabled: boolean;
  is_exceeded: boolean;
  quota_unit: STORAGE_QUOTA_UNIT;
  threshold_unit: STORAGE_QUOTA_UNIT;
  storage_size: number;
}

export interface UserStorageQuotaResponseDTO {
  is_user_enabled: boolean;
  user_quota: number;
  user_quota_unit: STORAGE_QUOTA_UNIT;
}

export interface StorageConfig {
  storage: {
    status: DMS_STATUS;
    centralizedDMS?: boolean;
    current_config?: {
      url: string;
      client_id: string;
      client_secret?: string;
      connected_on?: Date;
      deactivated_on?: Date;
    };
    new_config?: {
      url: string;
      client_id: string;
      client_secret?: string;
      valid_till: Date;
    };
  };
  recycle_bin: StorageRecycleBinRequestDTO;
  syncfusion_web_service: StorageServiceURLRequestDTO;
  storage_quota: StorageQuotaRequestDTO;
  user_quota: UserStorageQuotaRequestDTO;
  backblaze_config: string;
}

interface IDeleteStorageConfig {
  status: CONFIG_STATUS;
  url: string;
}

export interface StorageConfigRequestDTO {
  use_internal_storage?: boolean;
  config?: {
    url: string;
    client_id: string;
    client_secret?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class StorageSetupService {
  public readonly dataStore = new BehaviorSubject<StorageConfig | undefined>(
    undefined
  );

  constructor(private http: HttpClient) {}

  getAllConfigData(): Observable<CommonResponseDTO<StorageConfig>> {
    return this.http
      .get<CommonResponseDTO<StorageConfig>>(URLS.STORAGE_SETUP_STORAGE)
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  getExternalStorageConfigData(): Observable<
    CommonResponseDTO<IExternalStorageConfig[]>
  > {
    const url = generateURL({
      endpoint: ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_GET_ALL,
    });
    return this.http.get<CommonResponseDTO<IExternalStorageConfig[]>>(url);
  }

  getBackblazeConfigDataByAccountId(
    backblazeAccountId: string
  ): Observable<CommonResponseDTO<IExternalStorageConfig>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_GET_ONE,
      params: { id: backblazeAccountId },
    });

    return this.http.get<CommonResponseDTO<IExternalStorageConfig>>(url);
  }

  updateStorageConfig(
    data: StorageConfigRequestDTO
  ): Observable<CommonResponseDTO<any>> {
    return this.http
      .patch<CommonResponseDTO<any>>(URLS.STORAGE_SETUP_STORAGE, data)
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  enableExternalStorageConfig(
    data?: IExternalStorageConfig | EXTERNAL_STORAGE_LOCATION_ACTIONS
  ): Observable<CommonResponseDTO<IExternalStorageConfig[]>> {
    return this.http
      .patch<CommonResponseDTO<IExternalStorageConfig[]>>(
        URLS.ENABLE_EXTERNAL_STORAGE,
        data
      )
      .pipe(
        map((response) => {
          // If backend returned valid:false => throw and let the error handler catch it
          if ((response as any).data?.valid === false) {
            throw { message: (response as any).data.error };
          }
          return response;
        })
      );
  }

  deleteExternalStorageConfig(
    data?: IExternalStorageConfig
  ): Observable<CommonResponseDTO<IExternalStorageConfig[]>> {
    return this.http.patch<CommonResponseDTO<IExternalStorageConfig[]>>(
      URLS.DELETE_EXTERNAL_STORAGE,
      data
    );
  }

  editExternalStorageConfig(
    oldID: string,
    newID: string
  ): Observable<CommonResponseDTO<IExternalStorageConfig[]>> {
    const data = { oldID, newID };
    return this.http.patch<CommonResponseDTO<IExternalStorageConfig[]>>(
      URLS.EDIT_EXTERNAL_STORAGE,
      data
    );
  }

  updateStorageConfigLocation(
    data: StorageConfigRequestDTO
  ): Observable<CommonResponseDTO<any>> {
    return this.http
      .patch<CommonResponseDTO<any>>(URLS.STORAGE_SETUP_STORAGE_LOCATION, data)
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  updateRecycleBinConfig(
    data: StorageRecycleBinRequestDTO
  ): Observable<CommonResponseDTO<StorageConfig>> {
    return this.http
      .patch<CommonResponseDTO<StorageConfig>>(
        URLS.STORAGE_SETUP_RECYCLE_BIN,
        data
      )
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  updateSyncfusionWebServiceConfig(
    data: StorageServiceURLRequestDTO
  ): Observable<CommonResponseDTO<StorageConfig>> {
    return this.http
      .patch<CommonResponseDTO<StorageConfig>>(
        URLS.STORAGE_SETUP_SYNCFUSION_SERVICE_URL,
        data
      )
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  deleteIamConfig(
    data: IDeleteStorageConfig
  ): Observable<CommonResponseDTO<StorageConfig>> {
    return this.http
      .patch<CommonResponseDTO<StorageConfig>>(
        `${URLS.STORAGE_SETUP_STORAGE}/delete`,
        data
      )
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  updateStorageQuotaConfig(
    data: IStorageQuota
  ): Observable<CommonResponseDTO<StorageConfig>> {
    return this.http
      .patch<CommonResponseDTO<StorageConfig>>(
        `${URLS.STORAGE_SETUP_STORAGE_QUOTA}`,
        data
      )
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  updateBackblazeConfig(
    data: IExternalStorageConfig
  ): Observable<CommonResponseDTO<StorageConfig & IBackblazeConfigResponse>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SETUP_STORAGE_EXTERNAL_STORAGE_CONFIG_CREATE,
    });
    return this.http.patch<
      CommonResponseDTO<StorageConfig & IBackblazeConfigResponse>
    >(url, data);
  }
  setBackblazeBucket(
    bucket: IExternalStorageConfig,
    id?: string
  ): Observable<CommonResponseDTO<StorageConfig & IBackblazeConfigResponse>> {
    // Only append /id if id is present and non-empty
    const url = id
      ? `${URLS.EXTERNAL_STORAGE_LOCATION_SETUP_SELECT_BUCKET}/${id}`
      : `${URLS.EXTERNAL_STORAGE_LOCATION_SETUP_SELECT_BUCKET}`;
    return this.http.patch<
      CommonResponseDTO<StorageConfig & IBackblazeConfigResponse>
    >(url, bucket);
  }
  createBackblazeBucketConfig(
    data: IExternalStorageConfig,
    id?: string
  ): Observable<{
    success: boolean;
    error: string;
    data: StorageConfig & IBackblazeConfigResponse;
  }> {
    // Only append /id if id is present and non-empty
    const url = id
      ? `${URLS.EXTERNAL_STORAGE_LOCATION_SETUP_CREATE_BUCKET}/${id}`
      : `${URLS.EXTERNAL_STORAGE_LOCATION_SETUP_CREATE_BUCKET}`;
    return this.http.patch<{
      success: boolean;
      error: string;
      data: StorageConfig & IBackblazeConfigResponse;
    }>(url, data);
  }

  getTotalStorageSize(): Observable<any> {
    const url = `${URLS.STORAGE_SETUP_STORAGE}/size`;
    return this.http.get(url).pipe(
      tap((res) => {
        const currentDataStore = { ...this.dataStore.value };
        currentDataStore.storage_quota.storage_size = 0;

        res.data.forEach((storage) => {
          currentDataStore.storage_quota.storage_size += storage.totalSize;
        });

        this.dataStore.next(currentDataStore);
      })
    );
  }

  getExternalStorageConfigById(
    id: string
  ): Observable<CommonResponseDTO<IExternalStorageConfig>> {
    const url = `${URLS.EXTERNAL_STORAGE_CONFIG_GET_ONE}/${id}`;
    return this.http.get<CommonResponseDTO<IExternalStorageConfig>>(url);
  }
}
